import React, { useEffect, useState, useMemo } from "react";
import api from "../../services/apiClient.ts";
import { toast } from 'react-toastify';
import { useTranslation } from "react-i18next";
import { Plus, Search, Pencil, Trash2, Loader } from "lucide-react";

const API_BASE = "/v1";

export default function GroupsPaymentPage({ headersAuth, branchId }) {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ id:null, name:"" });
  const [loading, setLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get(`${API_BASE}/groups-payment`, {
        params: { q, branch_id: branchId },
        headers: headersAuth,
      });
      setItems(r.data?.data ?? r.data?.data?.data ?? r.data?.data ?? r.data ?? []);
    } catch(e) {
      console.error(e);
      toast.error(t("groupsPayment.loadFailed"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(()=>{ load(); /* eslint-disable-next-line */ }, [branchId]);

  const filteredItems = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return items;
    return items.filter(it => (it?.name ?? "").toLowerCase().includes(query));
  }, [items, q]);

  function openAdd(){ setForm({id:null,name:""}); setShowModal(true); }
  function openEdit(row){ setForm({id:row.id,name:row.name}); setShowModal(true); }
  function close(){ setShowModal(false); }

  async function save(){
    try {
      if (!form.name.trim()) return toast.error(t("groupsPayment.nameRequired"));
      if (!form.id) {
        await api.post(`${API_BASE}/groups-payment`, {name:form.name}, {headers: headersAuth});
        toast.success(t("groupsPayment.added"));
      } else {
        await api.put(`${API_BASE}/groups-payment/${form.id}`, {name:form.name}, {headers: headersAuth});
        toast.success(t("groupsPayment.updated"));
      }
      close(); load();
    } catch(e) {
      console.error(e);
      toast.error(t("groupsPayment.saveFailed"));
    }
  }

  async function remove(id){
    if (!window.confirm(t("groupsPayment.confirmDelete"))) return;
    try{
      await api.delete(`${API_BASE}/groups-payment/${id}`, {headers: headersAuth});
      toast.success(t("groupsPayment.deleted"));
      load();
    }catch(e){
      console.error(e);
      toast.error(t("groupsPayment.deleteFailed"));
    }
  }

  return (
    <div className="main-content p-6">
      <div className="w-full mx-auto space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="page-title text-2xl font-semibold text-gray-900 dark:text-gray-100">
              {t("groupsPayment.title")}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {t("groupsPayment.subtitle")}
            </p>
          </div>
          <button onClick={openAdd} className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary-600 text-white hover:bg-primary-700">
            <Plus className="w-4 h-4" />
            {t("groupsPayment.addType")}
          </button>
        </div>

        <div className="search-container">
          <div className="relative w-full md:w-96">
            <Search className="absolute top-1/2 -translate-y-1/2 ltr:left-3 rtl:right-3 w-4 h-4 text-gray-400" />
            <input
              className="w-full h-10 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 pl-10 rtl:pl-3 rtl:pr-10 focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder={t("groupsPayment.searchPlaceholder")}
              value={q}
              onChange={e=>setQ(e.target.value)}
            />
          </div>
        </div>

        <div className="app-table-wrap shadow-sm">
          <table className="app-table w-full">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 text-right rtl:text-right">#</th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 text-right rtl:text-right">{t("groupsPayment.name")}</th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 text-right rtl:text-right">{t("groupsPayment.actions")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
              {filteredItems.length===0 ? (
                <tr>
                  <td colSpan={3} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">{t("groupsPayment.noData")}</td>
                </tr>
              ) : filteredItems.map((it,idx)=>(
                <tr key={it.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200 text-right rtl:text-right">{idx+1}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 text-right rtl:text-right">{it.name}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-3 justify-end text-right rtl:justify-start">
                      <button onClick={()=>openEdit(it)} className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700 ">
                        <Pencil className="w-4 h-4" />
                        <span className="text-sm">{t("groupsPayment.editType")}</span>
                      </button>
                      <button onClick={()=>remove(it.id)} className="inline-flex items-center gap-1 text-red-600 hover:text-red-700 ">
                        <Trash2 className="w-4 h-4" />
                        <span className="text-sm">{t("common.delete")}</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {showModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-900 rounded-lg w-full max-w-lg border border-gray-200 dark:border-gray-800 shadow-lg">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {form.id ? t("groupsPayment.editType") : t("groupsPayment.addType")}
                </h3>
                <button onClick={close} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">×</button>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">{t("groupsPayment.name")}</label>
                  <input
                    className="w-full h-10 px-3 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:outline-none"
                    value={form.name}
                    onChange={e=>setForm(f=>({...f, name:e.target.value}))}
                  />
                </div>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-800 flex justify-end gap-3">
                <button onClick={close} className="px-4 py-2 rounded-md border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800">
                  {t("common.cancel")}
                </button>
                <button onClick={save} className="px-4 py-2 rounded-md bg-primary-600 text-white hover:bg-primary-700">
                  {t("common.save")}
                </button>
              </div>
            </div>
          </div>
        )}

        {loading && (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/30">
            <Loader className="w-10 h-10 text-primary-600 animate-spin" />
          </div>
        )}
      </div>
    </div>
  );
}
